import { Box, Flex, Link, Text } from '@chakra-ui/react';

import { Loading } from '@revelio/core';

import { useCurrencyPreference } from '../../../../../shared/user-preferences/hooks/useCurrencyPreference';
import { normalizeProfileUrl } from '../../../utils/normalizeProfileUrl';
import {
  MarkdownDescription,
  shouldRenderMarkdown,
} from '../MarkdownDescription/MarkdownDescription';
import { formatSalaryRange, relativePostedAt } from './postingFormat';
import type { FullPostingInfoData } from './types';
import { useFormattedPostingDescription } from './useFormattedPostingDescription';

const TEXT_COLOR = 'text.primary';

interface FullPostingInfoProps {
  data: FullPostingInfoData;
}

const LABEL_WIDTH = '110px';

const InfoRow = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <Flex gap="12px" align="baseline" marginBottom="16px">
    <Text
      fontSize="12px"
      fontWeight={600}
      color={TEXT_COLOR}
      minWidth={LABEL_WIDTH}
      flexShrink={0}
    >
      {label}
    </Text>
    <Box flex={1} fontSize="12px" fontWeight={400} color={TEXT_COLOR}>
      {children}
    </Box>
  </Flex>
);

export const FullPostingInfo = ({ data }: FullPostingInfoProps) => {
  // Salary is already converted to the preferred currency upstream (in
  // PostingDiscoveryDataProvider). Format with the shared currency formatter so
  // the value is rounded/abbreviated the same way Talent Discovery + the posting
  // table render salaries (e.g. "$60.5k").
  const { code: currencyCode } = useCurrencyPreference();
  const salaryText = data.salary
    ? formatSalaryRange(0, data.salary, currencyCode)
    : undefined;
  const relativePosted = data.postedAt ? relativePostedAt(data.postedAt) : '';
  const postedText = relativePosted ? `Posted ${relativePosted}` : undefined;
  const postingUrl = data.url ? normalizeProfileUrl(data.url) : null;
  const {
    description: formattedDescription,
    isFormatted,
    isFormatting,
  } = useFormattedPostingDescription(data.description ?? '');

  return (
    <Flex
      direction="column"
      minHeight="100%"
      padding="0 20px"
      backgroundColor="white"
      color={TEXT_COLOR}
      fontFamily="Source Sans Pro, sans-serif"
      data-testid="full-posting-info"
    >
      <Text fontSize="16px" fontWeight={700} marginTop={0} marginBottom="16px">
        Job details
      </Text>

      {salaryText && <InfoRow label="Salary">{salaryText}</InfoRow>}
      {data.jobType && <InfoRow label="Job Type">{data.jobType}</InfoRow>}
      {data.location && <InfoRow label="Work Setting">{data.location}</InfoRow>}
      {postingUrl && (
        <InfoRow label="Posting URL">
          <Link
            href={postingUrl.href}
            isExternal
            rel="noopener noreferrer"
            color="lightBlue.600"
            wordBreak="break-all"
            data-testid="full-posting-url"
          >
            {postingUrl.displayValue}
          </Link>
        </InfoRow>
      )}
      {data?.benefits && data?.benefits?.length > 0 && (
        <InfoRow label="Benefits">
          <Flex flexWrap="wrap" gap="6px">
            {data?.benefits.map((benefit, idx) => (
              <Text
                as="span"
                key={`benefit-${idx}`}
                padding="1px 5px 2px"
                borderRadius="4px"
                border="1px solid"
                borderColor="lightBlueGray"
                backgroundColor="white"
                fontSize="11px"
                lineHeight="16px"
                whiteSpace="nowrap"
              >
                {benefit}
              </Text>
            ))}
          </Flex>
        </InfoRow>
      )}

      {data.description && (
        <>
          <Box
            borderTop="1px dashed"
            borderTopColor="lightBlueGray"
            marginTop="20px"
            paddingTop="20px"
          >
            <Text fontSize="16px" fontWeight={700} color={TEXT_COLOR}>
              Full job description
            </Text>
          </Box>
          <Box
            fontSize="12px"
            fontWeight={400}
            lineHeight="18px"
            color={TEXT_COLOR}
            marginTop="8px"
            flex={1}
            display="flex"
            flexDirection="column"
          >
            {(() => {
              if (isFormatting) {
                return (
                  <Flex flex={1} justify="center" align="center">
                    <Loading size="md" />
                  </Flex>
                );
              }
              if (
                isFormatted ||
                shouldRenderMarkdown(formattedDescription, false)
              ) {
                return (
                  <MarkdownDescription description={formattedDescription} />
                );
              }
              return <Text whiteSpace="pre-wrap">{formattedDescription}</Text>;
            })()}
          </Box>
        </>
      )}

      {postedText && (
        <Text
          fontSize="11px"
          color={TEXT_COLOR}
          opacity={0.5}
          marginTop="auto"
          paddingTop="24px"
          marginBottom="-12px"
        >
          {postedText}
        </Text>
      )}
    </Flex>
  );
};
